"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// Sources:
//   • USDA FSIS — "Freezing and Food Safety": 0°F target; FIFO rotation;
//     organize by category and date.
//     https://www.fsis.usda.gov/food-safety/safe-food-handling-and-preparation/food-safety-basics/freezing-and-food-safety
//   • Manufacturer install/use guides for chest freezers (GE, Frigidaire,
//     Whirlpool): hanging/sliding basket dimensioning, drain plug clearance,
//     1" airflow gap recommendation.
//   • DOE/ENERGY STAR refrigerator & freezer guidance: keep freezer 70-85%
//     full for thermal mass efficiency.
//   • OSHA cold storage ergonomics (1910.176): clear aisles, weight limits
//     for routinely-lifted bins (~50 lb practical limit).

const SECTIONS = [
  { id: "why", t: "Why baskets matter" },
  { id: "count", t: "How many you need" },
  { id: "types", t: "Basket types" },
  { id: "zones", t: "Zone layout" },
  { id: "habits", t: "Habits that hold" },
] as const

export function ChestFreezerBasketGuidePageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Chest Freezer Basket Guide"
      heroEyebrow="Food storage"
      heroTitlePre="Stop digging through your"
      heroTitleItalic="chest freezer."
      heroTitlePost=""
      heroSubtitle="A chest freezer without baskets becomes a frozen lucky-dip. Here's how to size, layer, and zone baskets so the food on the bottom doesn't get forgotten — and the freezer keeps running efficiently."
      heroMeta={["⏱ ~6 min read", "USDA + manufacturer guidance"]}
      heroShare={<ToolShareBar title="Chest Freezer Basket Guide" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            Quick answer
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Most chest freezers ship with one hanging basket. Plan on{" "}
            <em className="italic text-[#ff9951]">one basket per 5 cu ft</em> for accessible storage.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Target temp
              </div>
              <div className="text-[22px] font-black tracking-tight">0°F</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Fill level
              </div>
              <div className="text-[22px] font-black tracking-tight">70–85%</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "How many baskets do I actually need?",
          answer:
            "Roughly one basket per 5 cu ft of freezer capacity. A 7 cu ft unit works well with 1–2 baskets, a 15 cu ft unit with 3, and a 22 cu ft unit with 4–5. More than that and you lose the deep-storage advantage chest freezers were designed for.",
        },
        {
          question: "Do baskets reduce my usable capacity?",
          answer:
            "Wire baskets sacrifice ~5–8% of usable volume to the basket frame and the airspace under hanging units. That's normally a worthwhile trade for the time saved finding food and the FIFO rotation it enables.",
        },
        {
          question: "Will baskets affect freezer efficiency?",
          answer:
            "Only at extremes. DOE recommends keeping a freezer 70–85% full so the thermal mass holds temperature between compressor cycles. Baskets help you organize that mass; they don't change it. Avoid blocking the manufacturer-marked airflow path or the drain plug.",
        },
        {
          question: "Where can I buy replacement baskets?",
          answer:
            "Manufacturer parts (Frigidaire, GE, Whirlpool, Midea) are the safest fit because they match the lid clearance and side-rail spacing your unit was designed around. Universal wire baskets sold by appliance retailers work if you measure the inside lip-to-lip width and lid-to-rail drop first.",
        },
        {
          question: "What's the maximum I should load in one basket?",
          answer:
            "Keep individual baskets under ~30–40 lb so they're safe to lift one-handed. OSHA cold-storage ergonomic guidance treats ~50 lb as a practical ceiling for routinely-lifted bins — and a chest freezer demands awkward over-the-rim lifting, which lowers that ceiling further.",
        },
      ]}
      relatedTools={[
        { title: "Freezer Burn Prevention", category: "Food Storage", description: "Stop dehydration before it starts.", href: "/tools/freezer-burn-prevention-guide", time: "~5 min" },
        { title: "Freezer Meat Storage", category: "Food Storage", description: "USDA quality windows by cut.", href: "/tools/freezer-meat-storage-calculator", time: "~30 sec" },
        { title: "Auto vs Manual Defrost", category: "Energy & Cost", description: "Which holds 0°F better.", href: "/tools/auto-vs-manual-defrost-cost-calculator", time: "~30 sec" },
        { title: "Freezer Capacity Calculator", category: "Sizing", description: "What size you actually need.", href: "/tools/freezer-storage-capacity-calculator", time: "~45 sec" },
      ]}
      cta={{
        title: "Outgrowing your chest freezer?",
        body: "If baskets are full and you're still stacking on top of them, it's time to size up. Browse our chest freezer collection.",
        buttonLabel: "Browse chest freezers",
        buttonHref: "/collections/chest-freezers",
      }}
    >
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          <article>
            <GuideTool
              eyebrow="Interactive planner"
              title="Plan your basket setup"
              intro="Answer three questions about your freezer and how you use it — get a basket count and an arrangement that keeps the bottom layers reachable."
              questions={[
                            {
                              id: "size",
                              label: "How big is the chest freezer?",
                              options: [
                                { value: "small", label: "Under 7 cu ft" },
                                { value: "mid", label: "7\u201314.9 cu ft" },
                                { value: "large", label: "15+ cu ft" }
                              ],
                            },
                            {
                              id: "load",
                              label: "What mostly goes in it?",
                              options: [
                                { value: "meat", label: "Bulk meat" },
                                { value: "mixed", label: "Mixed family stock" },
                                { value: "harvest", label: "Garden harvest / seasonal" }
                              ],
                            },
                            {
                              id: "access",
                              label: "How often do you dig into it?",
                              options: [
                                { value: "daily", label: "Most days" },
                                { value: "weekly", label: "About weekly" },
                                { value: "monthly", label: "A few times a season" }
                              ],
                            }
              ]}
              compute={(a) => {
                            const baskets = a.size === "small" ? "1 basket" : a.size === "mid" ? "2 baskets" : "3\u20134 baskets"
                            const arrangement =
                              a.load === "meat"
                                ? "Keep whole primals and long-term cuts on the floor, and use the baskets for this month's portions \u2014 ground, steaks, and anything thawing next."
                                : a.load === "harvest"
                                ? "Bag produce flat, stand the frozen sheets vertically like files on the floor, and reserve baskets for the odd shapes \u2014 berries, stock, sauces."
                                : "Zone the floor by category (proteins one end, breads and vegetables the other) and give each basket one grab-first category: snacks, weeknight proteins, breakfast."
                            const rotation =
                              a.access === "daily"
                                ? "With daily access, lift-out baskets earn their keep \u2014 pick handled baskets that clear the rim easily."
                                : a.access === "weekly"
                                ? "Weekly diggers should date everything on the lid surface with tape \u2014 first thing you see, no excavation to check."
                                : "For seasonal access, add a lid-taped inventory list and cross items off \u2014 the freezer stays accurate between visits."
                            return { tone: "good", verdict: baskets + " for this setup", detail: arrangement + " " + rotation }
                          }}
              ctaLabel="Plan my baskets"
              footnote="Sizing bands follow the capacity classes in the guide below."
            />
            <section id="why" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Why baskets matter in a chest freezer
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Chest freezers are vertical archives. Anything below the top six inches becomes
                effectively invisible. Without baskets, you end up rediscovering food months past
                its USDA quality window — bagged in the same dark corner where it was buried.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Baskets force a system. They define the top tier as a working surface, surface
                older items for FIFO rotation, and let you lift entire categories out at once when
                you need to dig deeper.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    Don&apos;t block the drain plug
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    Most chest freezers have a drain plug on one end of the floor. Manufacturer
                    install guides reserve that quadrant for items you can quickly remove for
                    defrost. Don&apos;t bury it under stacked bulk meat.
                  </p>
                </div>
              </div>
            </section>

            <section id="count" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                How many baskets you need
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                The practical rule from manufacturer use-and-care guides: one basket per ~5 cu ft
                of usable capacity, with a hard ceiling of about 5 baskets even on the largest
                consumer units. Beyond that you start cannibalizing the bulk-storage purpose of
                the freezer.
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Freezer size
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Recommended baskets
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Mix
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Under 5 cu ft", "1", "1 hanging"],
                      ["5–9 cu ft (compact)", "2", "1 hanging + 1 sliding"],
                      ["10–14 cu ft (mid)", "3", "2 hanging + 1 sliding"],
                      ["15–19 cu ft (large)", "4", "2 hanging + 2 sliding"],
                      ["20+ cu ft (XL)", "5", "2 hanging + 2 sliding + 1 divider"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[1]}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-600">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[13px] text-gray-500 mt-4">
                Source: typical chest-freezer use-and-care guides (Frigidaire, GE, Whirlpool).
                Verify against your model&apos;s lid clearance and side-rail spacing before
                ordering.
              </p>
            </section>

            <section id="types" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Basket types and what each is for
              </h2>
              <div className="space-y-3">
                {[
                  { t: "Hanging wire basket", d: "Rests on the side rails near the top. Holds the most-used items — bread, frozen veg, ice. Lifts straight out for full access to the layer below." },
                  { t: "Sliding basket", d: "Sits on a second tier of rails about midway down. Slides side-to-side to expose the floor without lifting. Best for medium-frequency items like proteins and meal-prep." },
                  { t: "Divider basket", d: "Floor-level open bin used to keep categories from migrating. Prevents the bottom from becoming a single mystery layer of bagged food." },
                  { t: "Stackable bins", d: "Aftermarket plastic bins (clear, ideally) for the floor. Useful when categories are sharply defined: game meat by animal, batch-cook meals by date." },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(255,153,81,0.04)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#ff9951" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="zones" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                A working three-zone layout
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Top zone — hanging baskets", d: "Daily and weekly items. Bread, ice, frozen vegetables, ready-to-heat meals, ice packs. The contents of this layer should turn over completely every 2–4 weeks." },
                  { n: "02", t: "Middle zone — sliding baskets", d: "Monthly items. Vacuum-sealed proteins, frozen pizzas, batched soups. Slide aside to access the floor; rotate front-to-back as you reload from the grocery." },
                  { n: "03", t: "Bottom zone — divider baskets / floor bins", d: "Bulk and long-hold items. Whole turkeys, bulk meat purchases, garden harvest. Date every package and keep an inventory list taped inside the lid." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="habits" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Habits that hold the system together
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Date every package — month and year, in permanent marker",
                  "Tape an inventory list inside the lid; update it as you reload",
                  "Reload front-to-back so older items rotate to the top",
                  "Keep the freezer 70–85% full (DOE efficiency target)",
                  "Leave the drain-plug quadrant accessible",
                  "Cap individual baskets at ~30–40 lb for safe lifting",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>
          </article>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Built for organization"
          title="Chest freezers with manufacturer baskets"
          description="Models that ship with a hanging basket and side rails ready for more."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
